import type { DependentRelation, FundPolicy } from '../../../core/fundPolicy/schema';

/**
 * Editing the fund policy from the settings screen.
 *
 * Pure: what the committee is allowed to set, and what the values mean, is
 * decided here and tested without rendering (§5.4). The screen shows the
 * result.
 */

export interface SettingsFormValues {
  /** `YYYY-MM-DD`, as a date input gives them. */
  readonly opensOn: string;
  readonly closesOn: string;
  readonly submissionsEnabled: boolean;
  readonly maxDependents: string;
  readonly childMaxAge: string;
  /** Which relations require a proof document, keyed by relation. */
  readonly proofRequired: Readonly<Partial<Record<string, boolean>>>;
  /** Which relations the fund covers at all. */
  readonly allowed: Readonly<Partial<Record<string, boolean>>>;
  /** Annual fund cost per covered person, in whole rials. */
  readonly costPerPerson: string;
  /** Annual ceiling the fund pays per person, in whole rials. */
  readonly ceilingPerPerson: string;
}

export type SettingsFieldErrors = Partial<Record<keyof SettingsFormValues, string>>;

/**
 * Yemeni Rial, stored in fils — two decimal places, as the committee decided.
 *
 * The screen asks for whole rials because that is how the fund's figures are
 * written down; storing fils keeps arithmetic exact.
 */
const FILS_PER_RIAL = 100;

/** Matches `moneyFilsSchema`. Above this a figure is a typo, not a budget. */
const MAX_FILS = 1_000_000_000_000;

const TIME_ZONE_SUFFIX = '+03:00';

/** The whole day is included: a window closing "on" a date closes at its end. */
export function toWindowInstants(opensOn: string, closesOn: string) {
  return {
    opensAt: `${opensOn}T00:00:00${TIME_ZONE_SUFFIX}`,
    closesAt: `${closesOn}T23:59:59${TIME_ZONE_SUFFIX}`,
  };
}

export function fromPolicy(policy: FundPolicy): SettingsFormValues {
  const childRule = policy.dependentRules.find((rule) => rule.relation === 'son');
  const tier = policy.coverageTiers[0];

  return {
    opensOn: policy.declarationWindow.opensAt.slice(0, 10),
    closesOn: policy.declarationWindow.closesAt.slice(0, 10),
    submissionsEnabled: policy.employeeSubmissionsEnabled,
    maxDependents: String(policy.maxDependentsPerEmployee),
    childMaxAge: String(childRule?.maxAgeYears ?? ''),
    proofRequired: Object.fromEntries(
      policy.dependentRules.map((rule) => [rule.relation, rule.requiresProofDocument]),
    ),
    allowed: Object.fromEntries(
      policy.dependentRules.map((rule) => [rule.relation, rule.allowed]),
    ),
    costPerPerson: String(Math.round((tier?.annualCostPerPersonFils ?? 0) / FILS_PER_RIAL)),
    ceilingPerPerson: String(Math.round((tier?.annualCeilingFils ?? 0) / FILS_PER_RIAL)),
  };
}

function wholeNumber(raw: string, max: number): number | null {
  const value = Number(raw.trim());
  return Number.isInteger(value) && value >= 0 && value <= max ? value : null;
}

export function validateSettings(values: SettingsFormValues): {
  readonly errors: SettingsFieldErrors;
  readonly valid: boolean;
} {
  const errors: SettingsFieldErrors = {};

  if (!/^\d{4}-\d{2}-\d{2}$/.test(values.opensOn)) errors.opensOn = 'حدد تاريخ الفتح';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(values.closesOn)) errors.closesOn = 'حدد تاريخ الإغلاق';

  if (!errors.opensOn && !errors.closesOn && values.closesOn < values.opensOn) {
    errors.closesOn = 'تاريخ الإغلاق يجب أن يكون بعد تاريخ الفتح';
  }

  if (wholeNumber(values.maxDependents, 30) === null) {
    errors.maxDependents = 'عدد بين 0 و 30';
  }

  // Blank means no age limit for children, which is a real choice.
  if (values.childMaxAge.trim() !== '' && wholeNumber(values.childMaxAge, 120) === null) {
    errors.childMaxAge = 'عمر بين 0 و 120، أو اتركه فارغًا';
  }

  if (wholeNumber(values.costPerPerson, MAX_FILS / FILS_PER_RIAL) === null) {
    errors.costPerPerson = 'مبلغ غير صالح';
  }
  if (wholeNumber(values.ceilingPerPerson, MAX_FILS / FILS_PER_RIAL) === null) {
    errors.ceilingPerPerson = 'مبلغ غير صالح';
  }

  return { errors, valid: Object.keys(errors).length === 0 };
}

/**
 * Applies the edits onto the stored policy.
 *
 * Read-modify-write of the whole document, deliberately: declaration items and
 * anything else this screen does not offer are carried through untouched. A
 * screen that rebuilt the policy from its own fields would silently delete
 * whatever it had not been taught about.
 */
export function applySettings(
  policy: FundPolicy,
  values: SettingsFormValues,
): Omit<FundPolicy, 'updatedAt' | 'updatedBy'> {
  // The author and timestamp are the server's to set (§4.2), so they are
  // dropped here. The two names are prefixed because nothing may read them —
  // that is the point of dropping them.
  const { updatedAt: _droppedAt, updatedBy: _droppedBy, ...rest } = policy;
  void _droppedAt;
  void _droppedBy;

  const maxAge = values.childMaxAge.trim() === '' ? null : Number(values.childMaxAge);

  return {
    ...rest,
    declarationWindow: toWindowInstants(values.opensOn, values.closesOn),
    employeeSubmissionsEnabled: values.submissionsEnabled,
    maxDependentsPerEmployee: Number(values.maxDependents),
    dependentRules: policy.dependentRules.map((rule) => {
      const isChild: boolean = rule.relation === 'son' || rule.relation === 'daughter';
      const relation: DependentRelation = rule.relation;

      return {
        ...rule,
        allowed: values.allowed[relation] ?? rule.allowed,
        requiresProofDocument: values.proofRequired[relation] ?? rule.requiresProofDocument,
        // `exactOptionalPropertyTypes` means an absent limit must be an absent
        // key, not an undefined one.
        ...(isChild && maxAge !== null ? { maxAgeYears: maxAge } : {}),
      };
    }),
    coverageTiers: policy.coverageTiers.map((tier, index) =>
      index === 0
        ? {
            ...tier,
            annualCostPerPersonFils: Number(values.costPerPerson) * FILS_PER_RIAL,
            annualCeilingFils: Number(values.ceilingPerPerson) * FILS_PER_RIAL,
          }
        : tier,
    ),
  };
}
